import React, { useState } from "react";
import styled from "@emotion/styled";
import * as Sentry from "@sentry/react";
import { Progress } from "antd5";
import { Flex, Text } from "styles/utility-components";
import userflow from "userflow.js";

import PartCard, { PartType } from "components/homepage/PartCard";
import Button from "components/ui/button/Button";
import { assertCurrentUser } from "lib/currentUser";
import { UpdateUserRequest_MainGoalEnum_1 } from "lib/generated/app-service-gql/graphql";
import { useUpdateUser } from "lib/hooks/api/users/useUpdateUser";

function toMainGoalEnum(mainGoal: PartType) {
  const mainGoalEnumMap: Record<PartType, UpdateUserRequest_MainGoalEnum_1> = {
    Strategy: UpdateUserRequest_MainGoalEnum_1.Strategy,
    Pipeline: UpdateUserRequest_MainGoalEnum_1.Pipeline,
    Tenders: UpdateUserRequest_MainGoalEnum_1.Tenders,
    Bids: UpdateUserRequest_MainGoalEnum_1.Bids,
  };
  return mainGoalEnumMap[mainGoal];
}

function convertToCapitalCase(key: PartType) {
  const str = key.replace(/-/g, " ");
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

type SelectMainGoalPageProps = {
  onSubmit: () => void;
};

export default function SelectMainGoalPage({ onSubmit }: SelectMainGoalPageProps) {
  const [mainGoal, setMainGoal] = useState<PartType | null>(null);

  const { mutate: updateUser } = useUpdateUser({
    onSuccess: onSubmit,
    onError: (e) => Sentry.captureException(e),
  });

  const handleNextClick = async () => {
    if (!mainGoal) return;
    const user = assertCurrentUser();
    updateUser({
      request: {
        guid: user.guid,
        mainGoal: toMainGoalEnum(mainGoal),
      },
    });
    try {
      await userflow.updateUser(
        {
          main_goal: convertToCapitalCase(mainGoal),
        },
        { signature: window.userflowUserSignature },
      );
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const parts: PartType[] = ["Strategy", "Pipeline", "Tenders", "Bids"];
  return (
    <Flex column>
      <ProgressBarContainer>
        <Progress percent={90} showInfo={false} />
      </ProgressBarContainer>
      <Text h1>What is your main goal with Stotles?</Text>
      <PartCardContainer>
        {parts.map((key) => (
          <PartCard key={key} partType={key} size="lg" onClick={() => setMainGoal(key)} />
        ))}
      </PartCardContainer>
      <ButtonContainer>
        <Button disabled={!mainGoal} onClick={handleNextClick}>
          Finish
        </Button>
      </ButtonContainer>
    </Flex>
  );
}

const ProgressBarContainer = styled.div({
  maxWidth: 200,
  marginBottom: 56,
});

const PartCardContainer = styled(Flex)({
  flexDirection: "column",
  gap: 16,
  margin: "40px 0",
  width: "100%",
});

export const ButtonContainer = styled.div({
  width: 80,
});
